import React, { useState } from 'react'
import { observer, useLocalStore } from 'mobx-react-lite'
import { FieldState } from 'formstate'
import { ForgotPasswordBody } from '@odiupsk/up-api-client'
import { Trans } from '@lingui/macro'
import styled from 'styled-components'
import { useRouter } from 'next/router'
import {
  Button,
  ButtonLayout,
  DialogContent,
  DialogFooter,
  DialogHeader,
  Input,
  ReachDialog,
} from '../../ui-kit'
import {
  email,
  length,
  login,
  mobilePhone,
  onlyNumericWithoutDecimal,
  required,
} from '../../utils/forms'

import { ApiAuthRepository } from '../../api/ApiAuthRepository'
import { ApiUsersRepository } from '../../api/ApiUsersRepository'
import { WidgetNewPassword } from './WidgetSetNewPassword'
import { fieldToInputProps } from '../../utils/form/fieldToInputProps'
import { getURLSearchParamNoArray } from '../../utils/routing'
import { i18n } from '../../utils/intl'
import { setErrorsFromResult } from '../../utils/extractMessageFromAxiosError'
import { useWillUnmount } from '../../utils/hooks/useWillUnmount'

const StyledNotice = styled.p`
  a {
    color: #4392f1;
    text-decoration: underline;
  }
`

export const FORGOTTEN_PASSWORD_MODAL_ID = 'forgotten-password-modal'

function isPhone(val: string) {
  return mobilePhone(val) == null
}

function isEmail(val: string) {
  return email(val) == null
}

function isPan(val: string) {
  return length(16)(val) == null && onlyNumericWithoutDecimal(val) == null
}

export const ModalForgottenPassword: React.FC<{
  close: () => void
}> = observer(({ close }) => {
  const router = useRouter()

  // const showForgottenPassword = getURLSearchParamNoArray(router.query.showForgottenPassword)
  const token = getURLSearchParamNoArray(router.query.token)

  const [idField] = useState(() =>
    new FieldState('').enableAutoValidation().validators(required, login)
  )

  const [smsCodeField] = useState(() =>
    new FieldState('').enableAutoValidation().validators(required)
  )

  const [newPassField] = useState(() =>
    new FieldState('').enableAutoValidation().validators(required)
  )

  const state = useLocalStore(
    args => ({
      step: 'initial' as
        | 'initial'
        | 'show-email-channel'
        | 'set-sms-code'
        | 'show-mail-channel'
        | 'error'
        | 'set-password'
        | 'ok',
      sending: false,
      message: undefined as undefined | string,
      channel: undefined as undefined | 'mail' | 'email' | 'phone',

      reset() {
        this.step = 'initial'
        state.message = undefined
        this.sending = false
        this.channel = undefined
      },
      setFromId(id: number, message?: string) {
        // type: 1 = email, 2 = SMS, 3 = poštou
        if (id === 1) {
          this.step = 'show-email-channel'
          this.channel = 'email'
        } else if (id === 2) {
          this.step = 'set-sms-code'
          this.channel = 'phone'
        } else if (id === 3) {
          this.step = 'show-mail-channel'
          this.channel = 'mail'
        } else {
          this.step = 'error'
        }
        this.message = message
      },

      async handleConfirm() {
        if (this.step === 'initial') state.sendPasswordResetRequest()
        else if (this.step === 'set-sms-code') await state.setSMSConfirmCode()
        else if (this.step === 'set-password') await state.sendResetPassword()
      },

      get confirmButtonLabel() {
        if (this.step === 'set-password') return i18n.t`Potvrdiť`
        return i18n.t`Odoslať`
      },

      get hasConfirmButton() {
        if (this.step === 'set-password' || this.step === 'initial' || this.step === 'set-sms-code')
          return true
        return false
      },

      get hasCancelButton() {
        if (
          this.step === 'show-email-channel' ||
          this.step === 'show-mail-channel' ||
          this.step === 'error' ||
          this.step === 'ok'
        )
          return true
        return false
      },

      /**
       * send new password and token / sms code
       */
      async sendResetPassword() {
        if (!this.channel || this.channel === 'mail') {
          console.warn('Invalid channel', this.channel)
          return
        }
        const { hasError } = await newPassField.validate()
        if (hasError) return console.warn(newPassField)

        try {
          await ApiUsersRepository.validatePasswordWithToken(
            newPassField.$,
            args.token,
            smsCodeField.$
          )
        } catch (err) {
          return
        }

        this.sending = true
        try {
          await ApiAuthRepository.resetPassword(this.channel, {
            password: newPassField.$,
            ...(this.channel === 'phone' && { code: smsCodeField.$ }),
            ...(this.channel === 'email' && { token: args.token }),
          })

          this.step = 'ok'
        } catch (err) {
          return
        } finally {
          this.sending = false
        }
      },

      async setSMSConfirmCode() {
        const { hasError } = await smsCodeField.validate()
        if (hasError) return console.warn(smsCodeField.error)

        this.step = 'set-password'
      },

      async sendPasswordResetRequest() {
        const valid = await idField.validate()
        if (valid.hasError) return

        // Force replace all spaces. There is a cross-browser issue with validator in modals.
        // Failed validation check will not produce empty email address in request body
        idField.$ = idField.$.replace(/ /g, '')

        const inputIsPhone = isPhone(idField.$)
        const inputIsEmail = isEmail(idField.$)
        const inputIsPan = isPan(idField.$)

        const body: ForgotPasswordBody = {
          email: inputIsEmail ? idField.$ : '',
          phone: inputIsPhone ? idField.$ : '',
          pan: inputIsPan ? idField.$ : '',
        }

        state.sending = true
        try {
          const response = await ApiAuthRepository.postForgotPassword(body)

          const type = response.type
          const message = (response as any).messages
            ?.map((m: { message: any }) => m.message)
            ?.join('\n')

          state.setFromId(type, message)
        } catch (err) {
          setErrorsFromResult<{ email: string; phone: string; pan: string }>(
            { email: idField, phone: idField, pan: idField },
            err as Error
          )
        } finally {
          state.sending = false
        }
      },
    }),
    { token }
  )

  useWillUnmount(() => {
    router.replace({
      pathname: router.pathname,
    })
  })

  const contentInitial = (
    <>
      <div className="mb-large text-color-grey-600">
        <Trans>Zadajte, prosím, kontaktný údaj, cez ktorý prebehne overenie.</Trans>
      </div>
      <div>
        <Input
          id="forgottenEmail"
          type="email"
          placeholder={i18n.t`meno.priezvisko@mail.com`}
          label={<Trans>Email, tel. číslo, alebo číslo karty</Trans>}
          isRequired
          {...fieldToInputProps(idField, (val: string) => val.trim())}
        />
        <StyledNotice className="form-message form-message--info mb">
          <span className="form-message__text">
            <Trans>
              V prípade, že nemáte overenú emailovú adresu, alebo telefónne číslo, zadajte číslo
              karty. Pokyny pre nastavenie nového hesla vám budú zaslané poštou.
            </Trans>
          </span>
        </StyledNotice>
      </div>
    </>
  )

  const contentSms = (
    <>
      <div className="mb-large text-color-grey-600">
        <Trans>
          Na Vaše tel. číslo bol poslaný kód na overenie. Zadajte, prosím, tento kód do políčka
          nižšie. Na zadanie kódu máte X minút.
        </Trans>
      </div>
      <div>
        <Input
          id="forgotten-email-sms-code"
          label={<Trans>Kód z sms</Trans>}
          isRequired
          {...fieldToInputProps(smsCodeField)}
        />
        <StyledNotice className="form-message form-message--info mb">
          <span className="form-message__text">
            <Trans>
              Ak ste neobdržali SMS s overovacím kódom, vyskúšajte{' '}
              <a
                onClick={() => {
                  state.step = 'initial'
                  state.message = undefined
                }}>
                opätovné zaslanie kódu
              </a>
              , alebo nás <a onClick={() => router.push('/contact')}>kontaktujte</a>.
            </Trans>
          </span>
        </StyledNotice>
      </div>
    </>
  )

  const contentMail = (
    <>
      <Trans>
        Nové heslo bude poslané poštou na vašu adresu. Po obdržaní sa prihláste týmto novým heslom,
        následne budete vyzvaný ku jeho zmene.
      </Trans>
    </>
  )

  const inputIsPhone = isPhone(idField.$)
  return (
    <ReachDialog id={FORGOTTEN_PASSWORD_MODAL_ID}>
      <DialogHeader title={i18n.t`Zabudnuté heslo`} close={close} />
      <DialogContent>
        {state.step === 'initial' && contentInitial}
        {state.step === 'show-email-channel' && (
          <div className="mb-large text-color-grey-600">
            {inputIsPhone ? (
              <Trans>
                Pomocou zadaného telefónneho čísla sme overili Vašu emailovú adresu. Pozrite si
                svoju emailovú schránku. Kliknite na odkaz a dokončite zmenu hesla. Odkaz je platný
                1 hodinu.
              </Trans>
            ) : (
              <Trans>
                Na zadanú emailovú adresu sme poslali odkaz na zmenu hesla. Kliknutím na odkaz
                dokončite proces zmeny emailovej adresy. Odkaz je platný 24 hodín.
              </Trans>
            )}
          </div>
        )}
        {state.step === 'set-sms-code' && contentSms}
        {state.step === 'show-mail-channel' && contentMail}
        {state.step === 'set-password' && <WidgetNewPassword field={newPassField} />}
        {state.step === 'error' && (state.message || <Trans>Nastala chyba</Trans>)}
        {state.step === 'ok' && (
          <div className="text-color-grey-600 mb-large mt-large">
            <Trans>Vaše heslo bolo úspešne zmenené</Trans>
          </div>
        )}
      </DialogContent>
      <DialogFooter>
        <ButtonLayout direction="horizontal" className="align-items-right">
          {state.hasCancelButton && (
            <Button onClick={close}>
              <Trans>Zavrieť</Trans>
            </Button>
          )}

          {state.hasConfirmButton && (
            <Button type="primary" onClick={state.handleConfirm} isDisabled={state.sending}>
              {state.confirmButtonLabel}
            </Button>
          )}
        </ButtonLayout>
      </DialogFooter>
    </ReachDialog>
  )
})

ModalForgottenPassword.displayName = 'ModalForgottenPassword'
